// src/pages/AdminAccesos.js
//
// Panel admin de monitoreo del uso del contenido. Tres tabs:
//
//   1. Scrapers sospechosos — usuarios con muchos accesos en poco
//      tiempo. Umbrales configurables.
//   2. Log de accesos — lista paginada con filtros por fecha.
//   3. Decodificar watermark — pega texto filtrado, extrae el
//      usuario cuyo id está codificado en los caracteres zero-width.
//
// Backend: /admin/accesos/* (AdminAccesosController). Requiere rol
// Administrador en educat_main.usuario_rol.

import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Chip,
  Stack,
  Divider,
  Grid,
} from '@mui/material';
import {
  Warning,
  History,
  Fingerprint,
  Search,
  Refresh,
} from '@mui/icons-material';
import { adminAccesosService } from '../services/api';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function TruncatedText({ text, max = 60 }) {
  if (!text) return <span style={{ color: '#999' }}>—</span>;
  const t = text.length > max ? text.slice(0, max) + '…' : text;
  return <span title={text}>{t}</span>;
}

// ─── TAB 1: Sospechosos ───────────────────────────────────────────

function SospechososTab() {
  const [dias, setDias] = useState(7);
  const [umbral, setUmbral] = useState(30);
  const [ventana, setVentana] = useState(10);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await adminAccesosService.sospechosos({ dias, umbral, ventanaMinutos: ventana });
      setData(r);
    } catch (e) {
      setError(e.message || 'Error consultando sospechosos.');
    } finally { setLoading(false); }
  }, [dias, umbral, ventana]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Lista usuarios que acumularon más de <strong>N accesos</strong> en una ventana
        de <strong>M minutos</strong> en los últimos <strong>D días</strong>. Ajuste los
        umbrales según el ruido que vea en los resultados.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Días a mirar atrás" type="number" fullWidth size="small"
            value={dias} onChange={(e) => setDias(Number(e.target.value) || 1)}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Umbral de accesos" type="number" fullWidth size="small"
            value={umbral} onChange={(e) => setUmbral(Number(e.target.value) || 1)}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Ventana (minutos)" type="number" fullWidth size="small"
            value={ventana} onChange={(e) => setVentana(Number(e.target.value) || 1)}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Button
            variant="contained" fullWidth sx={{ height: '100%' }}
            startIcon={<Search />} onClick={load} disabled={loading}
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {data.total === 0
              ? 'Ningún usuario superó los umbrales en ese rango — sin señales de scraping.'
              : `${data.total} usuario(s) con patrón sospechoso.`}
          </Typography>
          {data.items.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Accesos</TableCell>
                    <TableCell>Desde</TableCell>
                    <TableCell>Hasta</TableCell>
                    <TableCell align="right">Minutos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((row) => (
                    <TableRow key={row.usuarioId} hover>
                      <TableCell>{row.nombre || '—'}</TableCell>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell align="right">
                        <Chip label={row.accesos} color="warning" size="small" />
                      </TableCell>
                      <TableCell>{fmtDate(row.desde)}</TableCell>
                      <TableCell>{fmtDate(row.hasta)}</TableCell>
                      <TableCell align="right">{Number(row.duracionMin || 0).toFixed(1)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

// ─── TAB 2: Log de accesos ─────────────────────────────────────────

function LogTab() {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [pagina, setPagina] = useState(1);
  const tamano = 25;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await adminAccesosService.listar({
        desde: desde || undefined,
        hasta: hasta || undefined,
        pagina, tamano,
      });
      setData(r);
    } catch (e) {
      setError(e.message || 'Error consultando el log.');
    } finally { setLoading(false); }
  }, [desde, hasta, pagina]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Cada apertura de contenido queda registrada con IP y navegador. Útil para
        auditoría puntual (quién abrió qué, cuándo). Filtre por fecha para acotar.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Desde" type="datetime-local" fullWidth size="small"
            InputLabelProps={{ shrink: true }}
            value={desde} onChange={(e) => setDesde(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Hasta" type="datetime-local" fullWidth size="small"
            InputLabelProps={{ shrink: true }}
            value={hasta} onChange={(e) => setHasta(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            variant="contained" fullWidth sx={{ height: '100%' }}
            startIcon={<Refresh />} onClick={() => { setPagina(1); load(); }}
            disabled={loading}
          >
            {loading ? 'Cargando...' : 'Consultar'}
          </Button>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Contenido</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>User Agent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{fmtDate(row.fechaAcceso)}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{row.usuarioNombre || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.usuarioEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TruncatedText text={row.contenidoTitulo} max={40} />
                    </TableCell>
                    <TableCell>
                      {row.contenidoTipo && (
                        <Chip label={row.contenidoTipo} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell><code style={{ fontSize: '0.8rem' }}>{row.ip || '—'}</code></TableCell>
                    <TableCell>
                      <TruncatedText text={row.userAgent} max={50} />
                    </TableCell>
                  </TableRow>
                ))}
                {data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Sin registros en el rango seleccionado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data.total || 0}
            page={pagina - 1}
            onPageChange={(_, p) => { setPagina(p + 1); load(); }}
            rowsPerPage={tamano}
            rowsPerPageOptions={[tamano]}
          />
        </>
      )}
    </Box>
  );
}

// ─── TAB 3: Decodificar watermark ─────────────────────────────────

function WatermarkTab() {
  const [texto, setTexto] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const decodificar = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      const r = await adminAccesosService.decodeWatermark(texto);
      setResult(r);
    } catch (e) {
      setError(e.message || 'Error decodificando.');
    } finally { setLoading(false); }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Pegue aquí un fragmento de texto que crea que fue copiado del contenido
        de la plataforma. El sistema buscará caracteres invisibles de marca
        forense (<code>U+200B</code>, <code>U+200C</code>) y, si los encuentra,
        decodificará los primeros 8 bytes del ID del usuario que lo originó.
        Es un indicio probable, no una prueba absoluta.
      </Alert>

      <TextField
        label="Texto a analizar"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        fullWidth
        multiline
        minRows={6}
        placeholder="Pegue aquí el texto filtrado..."
        sx={{ mb: 2 }}
      />

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          onClick={decodificar}
          disabled={loading || !texto.trim()}
          startIcon={<Fingerprint />}
        >
          {loading ? 'Analizando...' : 'Decodificar marca'}
        </Button>
        <Button onClick={() => { setTexto(''); setResult(null); setError(''); }}>
          Limpiar
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

      {result && (
        <Paper variant="outlined" sx={{ mt: 3, p: 3 }}>
          {result.encontrado ? (
            <>
              <Chip label="Marca detectada" color="success" sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>Candidato más probable</Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Usuario</Typography>
                  <Typography variant="body1" fontWeight={600}>{result.usuario?.nombre || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{result.usuario?.email || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">User ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {result.usuario?.id || '—'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Prefijo hex decodificado (primeros 8 bytes del Guid)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {result.prefijoHex}
                  </Typography>
                </Box>
              </Stack>
            </>
          ) : (
            <>
              <Chip label="Sin coincidencia" color="warning" sx={{ mb: 2 }} />
              <Typography variant="body2">{result.mensaje}</Typography>
              {result.prefijoHex && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Prefijo hex decodificado (pero no mapea a ningún usuario de esta BD)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {result.prefijoHex}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}

// ─── Página principal ─────────────────────────────────────────────

export default function AdminAccesosPage() {
  const [tab, setTab] = useState(0);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Auditoría de contenido
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Monitoreo del uso del material educativo, detección de scraping y herramienta
        forense para identificar filtraciones.
      </Typography>

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab icon={<Warning />} iconPosition="start" label="Sospechosos" />
          <Tab icon={<History />} iconPosition="start" label="Log de accesos" />
          <Tab icon={<Fingerprint />} iconPosition="start" label="Decodificar marca" />
        </Tabs>
        <Divider />
        <Box sx={{ p: 3 }}>
          {tab === 0 && <SospechososTab />}
          {tab === 1 && <LogTab />}
          {tab === 2 && <WatermarkTab />}
        </Box>
      </Paper>
    </Container>
  );
}
